import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Search, RefreshCw, Plus, Building2, MapPin, Phone, Mail,
  Package, Calendar, AlertCircle, CheckCircle2, Clock, Filter,
  Download, Eye, ChevronRight, Edit, Loader2, Save, X, Trash2,
  BarChart3
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ClientRequirementsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role?.toLowerCase() === "admin";
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProductType, setFilterProductType] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterJobStage, setFilterJobStage] = useState("all");
  
  // Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingRequirement, setDeletingRequirement] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // R&D Form state
  const [showRDFormDialog, setShowRDFormDialog] = useState(false);
  const [newStatusRequirements, setNewStatusRequirements] = useState([]);
  const [loadingNewStatus, setLoadingNewStatus] = useState(false);
  const [selectedRDSample, setSelectedRDSample] = useState(null);
  const [rdFormData, setRDFormData] = useState({
    sampleNo: "",
    sampleLookupId: "",
    productName: "",
    productLookupId: "",
    testerName: "",
    testerLookupId: "",
    testPriority: "",
    sku: "",
    remarks: "",
    file: null
  });
  const [submittingRDForm, setSubmittingRDForm] = useState(false);
  const [productList, setProductList] = useState([]);
  const [labEmployees, setLabEmployees] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingTesters, setLoadingTesters] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Open edit dialog
  const openEditDialog = (req) => {
    setEditingRequirement({ ...req });
    setShowEditDialog(true);
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    setEditingRequirement(prev => ({ ...prev, [field]: value }));
  };

  // Save edited requirement
  const handleSaveEdit = async () => {
    if (!editingRequirement) return;
    
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/zoho/client-requirement/${encodeURIComponent(editingRequirement.sampleNo)}`,
        editingRequirement
      );
      
      if (response.data.success) {
        toast.success("Client Requirement updated successfully!");
        if (response.data.zohoSync?.success) {
          toast.success("✅ Synced to Zoho Creator");
        }
        setShowEditDialog(false);
        setEditingRequirement(null);
        fetchRequirements(false, false);
      } else {
        toast.error(response.data.error || "Failed to update requirement");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update requirement");
    } finally {
      setSaving(false);
    }
  };

  // Delete requirement (Admin only)
  const handleDelete = async () => {
    if (!deletingRequirement) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API}/zoho/client-requirement/${encodeURIComponent(deletingRequirement.sampleNo)}`,
        { params: { user_role: user?.role } }
      );
      
      if (response.data.success) {
        toast.success("Client Requirement deleted successfully!");
        setShowDeleteDialog(false);
        setDeletingRequirement(null);
        fetchRequirements(false, true);
      } else {
        toast.error(response.data.error || "Failed to delete requirement");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete requirement");
    } finally {
      setDeleting(false);
    }
  };

  // Fetch New Status requirements for R&D Form
  const fetchNewStatusRequirements = async () => {
    setLoadingNewStatus(true);
    try {
      const response = await axios.get(`${API}/zoho/client-requirements/new-status`);
      if (response.data.success) {
        setNewStatusRequirements(response.data.requirements || []);
      }
    } catch (error) {
      console.error("Error fetching new status requirements:", error);
      toast.error("Failed to load requirements with New status");
    } finally {
      setLoadingNewStatus(false);
    }
  };

  // Fetch Product List from Zoho
  const fetchProductList = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/zoho/product-list`);
      if (response.data.success || response.data.products) {
        // Filter out products with empty names and sort alphabetically
        const validProducts = (response.data.products || [])
          .filter(p => p.productName && p.productName.trim())
          .sort((a, b) => a.productName.localeCompare(b.productName));
        setProductList(validProducts);
      }
    } catch (error) {
      console.error("Error fetching product list:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Fetch Lab Employees from Zoho
  const fetchLabEmployees = async () => {
    setLoadingTesters(true);
    try {
      const response = await axios.get(`${API}/zoho/lab-employees`);
      if (response.data.employees) {
        setLabEmployees(response.data.employees || []);
      }
    } catch (error) {
      console.error("Error fetching lab employees:", error);
    } finally {
      setLoadingTesters(false);
    }
  };

  // Open R&D Form Dialog
  const openRDFormDialog = () => {
    setShowRDFormDialog(true);
    fetchNewStatusRequirements();
    fetchProductList();
    fetchLabEmployees();
  };

  // Open R&D Form Dialog with pre-selected sample
  const openRDFormForSample = (req) => {
    setShowRDFormDialog(true);
    fetchProductList();
    fetchLabEmployees();
    // Pre-select the sample
    const sampleData = {
      sampleNo: req.sampleNo,
      sampleLookupId: req.zohoId || "",
      productType: req.productType,
      clientName: req.clientName,
      companyName: req.companyName,
      category: req.category,
      subCategory: req.subCategory,
      jobStage: req.jobStage
    };
    setSelectedRDSample(sampleData);
    setRDFormData({
      sampleNo: req.sampleNo,
      sampleLookupId: req.zohoId || "",
      productName: "",
      productLookupId: "",
      testerName: "",
      testerLookupId: "",
      testPriority: "",
      sku: "",
      remarks: "",
      file: null
    });
    // Also fetch new status requirements in background
    fetchNewStatusRequirements();
  };

  // Handle sample selection in R&D Form
  const handleRDSampleSelect = (requirement) => {
    setSelectedRDSample(requirement);
    setRDFormData({
      sampleNo: requirement.sampleNo,
      sampleLookupId: requirement.sampleLookupId,
      productName: "",
      productLookupId: "",
      testerName: "",
      testerLookupId: "",
      testPriority: "",
      sku: "",
      remarks: "",
      file: null
    });
  };

  // Submit R&D Form
  const handleSubmitRDForm = async () => {
    if (!rdFormData.sampleNo) {
      toast.error("Please select a sample");
      return;
    }
    if (!rdFormData.productName) {
      toast.error("Please select a Product Name");
      return;
    }
    if (!rdFormData.testerName) {
      toast.error("Please select a Tester Name");
      return;
    }
    if (!rdFormData.testPriority) {
      toast.error("Please select a Test Priority");
      return;
    }

    setSubmittingRDForm(true);
    try {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append("sampleNo", rdFormData.sampleNo);
      formData.append("sampleLookupId", rdFormData.sampleLookupId || "");
      formData.append("productName", rdFormData.productName);
      formData.append("productLookupId", rdFormData.productLookupId || "");
      formData.append("testerName", rdFormData.testerName);
      formData.append("testerLookupId", rdFormData.testerLookupId || "");
      formData.append("testPriority", rdFormData.testPriority);
      formData.append("sku", rdFormData.sku || "");
      formData.append("remarks", rdFormData.remarks || "");
      
      if (rdFormData.file) {
        formData.append("file", rdFormData.file);
      }

      const response = await axios.post(`${API}/zoho/rd-form/submit`, formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (response.data.success) {
        // Check if file upload was successful
        const fileResult = response.data.fileUpload;
        if (rdFormData.file && fileResult) {
          if (fileResult.success) {
            toast.success("R&D Form submitted with file uploaded successfully!");
          } else {
            toast.success("R&D Form submitted! Note: File upload failed - " + (fileResult.error || "Unknown error"));
          }
        } else {
          toast.success("R&D Form submitted successfully!");
        }
        setShowRDFormDialog(false);
        setSelectedRDSample(null);
        setRDFormData({
          sampleNo: "",
          sampleLookupId: "",
          productName: "",
          productLookupId: "",
          testerName: "",
          testerLookupId: "",
          testPriority: "",
          sku: "",
          remarks: "",
          file: null
        });
        // Refresh requirements to update job stage
        fetchRequirements(false, true);
      } else {
        toast.error(response.data.message || "Failed to submit R&D Form");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit R&D Form");
    } finally {
      setSubmittingRDForm(false);
    }
  };

  // Fetch requirements from Zoho
  const fetchRequirements = async (showRefreshToast = false, forceRefresh = false) => {
    if (showRefreshToast) setRefreshing(true);
    else setLoading(true);
    
    try {
      // If force refresh, clear the cache first and enrich categories
      if (forceRefresh) {
        const refreshResponse = await axios.post(`${API}/zoho/client-requirements/refresh`);
        // If there are records missing category data, trigger enrichment
        if (refreshResponse.data.missingCategory > 0) {
          try {
            await axios.post(`${API}/zoho/client-requirements/enrich`);
          } catch (enrichError) {
            console.warn("Category enrichment partially completed:", enrichError);
          }
        }
      }
      
      const response = await axios.get(`${API}/zoho/client-requirements`);
      if (response.data.success) {
        // Flatten the clients data to show individual samples
        const allRequirements = [];
        response.data.clients.forEach(client => {
          client.samples.forEach(sample => {
            allRequirements.push({
              ...sample,
              clientName: client.clientName,
              companyName: client.companyName,
              email: client.email,
              phoneNumber: client.phoneNumber,
              location: client.location
            });
          });
        });
        setRequirements(allRequirements);
        if (showRefreshToast) {
          toast.success(`Loaded ${allRequirements.length} requirements from Zoho`);
        }
      }
    } catch (error) {
      console.error("Error fetching requirements:", error);
      toast.error("Failed to load requirements");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // On initial load, fetch from cache (don't force refresh to preserve API quota)
    // User can manually click "Sync from Zoho" button to refresh
    fetchRequirements(false, false);
    
    // Check for URL query params for filtering (from dashboard)
    const urlParams = new URLSearchParams(window.location.search);
    const stageParam = urlParams.get('stage');
    const clientParam = urlParams.get('client');
    if (stageParam) {
      setFilterJobStage(stageParam);
    }
    if (clientParam) {
      setSearchTerm(clientParam);
    }
  }, []);

  // Filter requirements
  const filteredRequirements = requirements.filter(req => {
    const matchesSearch = 
      (req.clientName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (req.companyName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (req.sampleNo?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    
    const matchesProductType = filterProductType === "all" || req.productType === filterProductType;
    const matchesPriority = filterPriority === "all" || req.priority === filterPriority;
    const matchesJobStage = filterJobStage === "all" || req.jobStage === filterJobStage;
    
    return matchesSearch && matchesProductType && matchesPriority && matchesJobStage;
  });

  // Get unique product types for filter
  const productTypes = [...new Set(requirements.map(r => r.productType).filter(Boolean))];
  
  // Get unique job stages for filter
  const jobStages = [...new Set(requirements.map(r => r.jobStage).filter(Boolean))];

  // Priority badge styling
  const getPriorityBadge = (priority) => {
    const styles = {
      High: "bg-red-100 text-red-700 border-red-200",
      Medium: "bg-amber-100 text-amber-700 border-amber-200",
      Low: "bg-green-100 text-green-700 border-green-200"
    };
    return styles[priority] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  // Job stage badge styling with more comprehensive statuses
  const getJobStageBadge = (stage) => {
    const styles = {
      // New / Initial stages
      "New": "bg-blue-100 text-blue-700 border-blue-200",
      "Pending": "bg-blue-100 text-blue-700 border-blue-200",
      
      // In Progress stages  
      "In Progress": "bg-amber-100 text-amber-700 border-amber-200",
      "Under R&D": "bg-purple-100 text-purple-700 border-purple-200",
      "R&D": "bg-purple-100 text-purple-700 border-purple-200",
      "Under Development": "bg-purple-100 text-purple-700 border-purple-200",
      
      // Testing stages
      "Testing": "bg-orange-100 text-orange-700 border-orange-200",
      "Under Testing": "bg-orange-100 text-orange-700 border-orange-200",
      "Quality Check": "bg-orange-100 text-orange-700 border-orange-200",
      
      // Review / Approval stages
      "Review": "bg-indigo-100 text-indigo-700 border-indigo-200",
      "Awaiting Approval": "bg-indigo-100 text-indigo-700 border-indigo-200",
      "Client Review": "bg-indigo-100 text-indigo-700 border-indigo-200",
      
      // Sample stages
      "Sample Ready": "bg-teal-100 text-teal-700 border-teal-200",
      "Sample Sent": "bg-teal-100 text-teal-700 border-teal-200",
      "Sample Approved": "bg-emerald-100 text-emerald-700 border-emerald-200",
      
      // Completed stages
      "Completed": "bg-green-100 text-green-700 border-green-200",
      "Approved": "bg-green-100 text-green-700 border-green-200",
      "Done": "bg-green-100 text-green-700 border-green-200",
      "Finalized": "bg-green-100 text-green-700 border-green-200",
      
      // Hold / Cancelled stages
      "On Hold": "bg-slate-100 text-slate-600 border-slate-200",
      "Hold": "bg-slate-100 text-slate-600 border-slate-200",
      "Cancelled": "bg-red-100 text-red-700 border-red-200",
      "Rejected": "bg-red-100 text-red-700 border-red-200"
    };
    return styles[stage] || "bg-slate-100 text-slate-600 border-slate-200";
  };

  return (
    <div className="p-6 md:p-8" data-testid="client-requirements-page">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-poppins font-bold text-slate-800">
              Client Requirements
            </h1>
            <p className="text-slate-500 font-poppins mt-1">
              View and manage all product requirements from clients
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => fetchRequirements(true, true)}
              disabled={refreshing}
              className="font-poppins"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Syncing...' : 'Sync from Zoho'}
            </Button>
            <Button
              onClick={() => navigate('/client-requirement-dashboard')}
              variant="outline"
              className="font-poppins border-vcc-gold text-vcc-gold hover:bg-vcc-gold/10"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <Button
              onClick={() => navigate('/client-requirement')}
              className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Requirement
            </Button>
            <Button
              onClick={openRDFormDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white font-poppins"
              data-testid="rd-form-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{requirements.length}</p>
              <p className="text-sm text-slate-500 font-poppins">Total Requirements</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {requirements.filter(r => r.priority === "High").length}
              </p>
              <p className="text-sm text-slate-500 font-poppins">High Priority</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {requirements.filter(r => r.jobStage === "In Progress" || r.jobStage === "R&D").length}
              </p>
              <p className="text-sm text-slate-500 font-poppins">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {requirements.filter(r => r.jobStage === "Completed").length}
              </p>
              <p className="text-sm text-slate-500 font-poppins">Completed</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by client, company, or sample no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 font-poppins"
              data-testid="search-requirements"
            />
          </div>
          <div className="flex gap-3">
            <Select value={filterProductType} onValueChange={setFilterProductType}>
              <SelectTrigger className="w-48 font-poppins">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Product Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Product Types</SelectItem>
                {productTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-40 font-poppins">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterJobStage} onValueChange={setFilterJobStage}>
              <SelectTrigger className="w-48 font-poppins">
                <SelectValue placeholder="Job Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {jobStages.map(stage => (
                  <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Active Filter Badge */}
        {filterJobStage !== "all" && (
          <div className="mt-3 flex items-center gap-2">
            <Badge className="bg-vcc-teal/10 text-vcc-teal border-vcc-teal/30 font-poppins">
              Filtered by: {filterJobStage}
              <button 
                onClick={() => setFilterJobStage("all")}
                className="ml-2 hover:text-vcc-teal/70"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
            <span className="text-sm text-slate-500 font-poppins">
              Showing {filteredRequirements.length} of {requirements.length} requirements
            </span>
          </div>
        )}
      </motion.div>

      {/* Requirements Table */}
      <motion.div 
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-vcc-teal animate-spin mb-4" />
            <p className="text-slate-500 font-poppins">Loading requirements from Zoho...</p>
          </div>
        ) : filteredRequirements.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-poppins mb-2">No requirements found</p>
            <p className="text-sm text-slate-400 font-poppins">
              {searchTerm || filterProductType !== "all" || filterPriority !== "all" || filterJobStage !== "all"
                ? "Try adjusting your filters"
                : "Submit a new requirement to get started"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-poppins font-semibold">Sample No</TableHead>
                  <TableHead className="font-poppins font-semibold">Client / Company</TableHead>
                  <TableHead className="font-poppins font-semibold">Product Type</TableHead>
                  <TableHead className="font-poppins font-semibold">Category</TableHead>
                  <TableHead className="font-poppins font-semibold">Sub-Category</TableHead>
                  <TableHead className="font-poppins font-semibold">Location</TableHead>
                  <TableHead className="font-poppins font-semibold">Priority</TableHead>
                  <TableHead className="font-poppins font-semibold">Status</TableHead>
                  <TableHead className="font-poppins font-semibold">R&D</TableHead>
                  <TableHead className="font-poppins font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequirements.map((req, index) => (
                  <TableRow key={`${req.sampleNo}-${index}`} className="hover:bg-slate-50">
                    <TableCell className="font-poppins font-medium text-vcc-teal">
                      {req.sampleNo || "-"}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-poppins font-medium text-slate-800">
                          {req.clientName || req.companyName || "Unknown"}
                        </p>
                        {req.companyName && req.clientName !== req.companyName && (
                          <p className="text-sm text-slate-500 font-poppins flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {req.companyName}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-poppins">
                        {req.productType || "-"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-700 font-poppins text-sm">
                        {req.category || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 font-poppins text-sm">
                        {req.subCategory || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-slate-600 font-poppins">
                        <MapPin className="w-3 h-3" />
                        {req.location || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getPriorityBadge(req.priority)} font-poppins border`}>
                        {req.priority || "-"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={req.jobStage || "New"}
                        onValueChange={async (value) => {
                          try {
                            const formData = new FormData();
                            formData.append("status", value);
                            const response = await axios.patch(
                              `${API}/zoho/client-requirements/${req.sampleNo}/status`,
                              formData
                            );
                            if (response.data.success) {
                              toast.success(`Status updated to "${value}"`);
                              // Update local state
                              setRequirements(prev => prev.map(r => 
                                r.sampleNo === req.sampleNo ? {...r, jobStage: value} : r
                              ));
                            } else {
                              toast.error(response.data.message || "Failed to update status");
                            }
                          } catch (error) {
                            toast.error("Failed to update status");
                            console.error("Status update error:", error);
                          }
                        }}
                      >
                        <SelectTrigger className={`w-[130px] h-8 text-xs ${getJobStageBadge(req.jobStage)}`}>
                          <SelectValue>{req.jobStage || "New"}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="Under R&D">Under R&D</SelectItem>
                          <SelectItem value="Sample Ready">Sample Ready</SelectItem>
                          <SelectItem value="Sample Sent">Sample Sent</SelectItem>
                          <SelectItem value="Feedback Received">Feedback Received</SelectItem>
                          <SelectItem value="Approved">Approved</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {(req.jobStage === "New" || !req.jobStage) && (
                        <Button
                          size="sm"
                          onClick={() => openRDFormForSample(req)}
                          className="bg-blue-600 hover:bg-blue-700 text-white font-poppins text-xs px-2 py-1"
                          data-testid={`rd-form-row-btn-${req.sampleNo}`}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          New
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => openEditDialog(req)}
                          style={{ backgroundColor: '#2563eb', color: 'white' }}
                          className="font-poppins hover:opacity-90 shadow-md"
                          data-testid={`edit-req-${req.sampleNo}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/client-requirements/${encodeURIComponent(req.sampleNo)}`)}
                          className="font-poppins"
                          data-testid={`view-req-${req.sampleNo}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingRequirement(req);
                              setShowDeleteDialog(true);
                            }}
                            className="font-poppins text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-req-${req.sampleNo}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </motion.div>

      {/* Footer Stats */}
      {!loading && filteredRequirements.length > 0 && (
        <motion.div 
          className="mt-4 text-sm text-slate-500 font-poppins text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Showing {filteredRequirements.length} of {requirements.length} requirements
        </motion.div>
      )}

      {/* Edit Client Requirement Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Client Requirement - {editingRequirement?.sampleNo}
            </DialogTitle>
          </DialogHeader>

          {editingRequirement && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Sample No</Label>
                  <Input value={editingRequirement.sampleNo || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Client Name</Label>
                  <Input 
                    value={editingRequirement.clientName || ""} 
                    onChange={(e) => handleEditChange("clientName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Company Name</Label>
                  <Input 
                    value={editingRequirement.companyName || ""} 
                    onChange={(e) => handleEditChange("companyName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Email</Label>
                  <Input 
                    value={editingRequirement.email || ""} 
                    onChange={(e) => handleEditChange("email", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Phone Number</Label>
                  <Input 
                    value={editingRequirement.phoneNumber || ""} 
                    onChange={(e) => handleEditChange("phoneNumber", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Location</Label>
                  <Input 
                    value={editingRequirement.location || ""} 
                    onChange={(e) => handleEditChange("location", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Product Type</Label>
                  <Select 
                    value={editingRequirement.productType || ""} 
                    onValueChange={(value) => handleEditChange("productType", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Skin Care Products">Skin Care Products</SelectItem>
                      <SelectItem value="Hair Care Products">Hair Care Products</SelectItem>
                      <SelectItem value="Oral Care Products">Oral Care Products</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Priority</Label>
                  <Select 
                    value={editingRequirement.priority || "Medium"} 
                    onValueChange={(value) => handleEditChange("priority", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Job Stage</Label>
                  <Select 
                    value={editingRequirement.jobStage || "New"} 
                    onValueChange={(value) => handleEditChange("jobStage", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Testing">Testing</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Dispatched">Dispatched</SelectItem>
                      <SelectItem value="On Hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Texture</Label>
                  <Input 
                    value={editingRequirement.texture || ""} 
                    onChange={(e) => handleEditChange("texture", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Color</Label>
                  <Input 
                    value={editingRequirement.color || ""} 
                    onChange={(e) => handleEditChange("color", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Odour/Fragrance</Label>
                  <Input 
                    value={editingRequirement.odourFragrance || ""} 
                    onChange={(e) => handleEditChange("odourFragrance", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Pack & Quantity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Packaging Preference</Label>
                  <Input 
                    value={editingRequirement.packagingPreference || ""} 
                    onChange={(e) => handleEditChange("packagingPreference", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Pack Size</Label>
                  <Input 
                    value={editingRequirement.packSize || ""} 
                    onChange={(e) => handleEditChange("packSize", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Required Quantity</Label>
                  <Input 
                    value={editingRequirement.quantity || ""} 
                    onChange={(e) => handleEditChange("quantity", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              {/* Remarks */}
              <div>
                <Label className="text-xs text-slate-600 font-poppins">Remarks</Label>
                <Textarea 
                  value={editingRequirement.remarks || ""} 
                  onChange={(e) => handleEditChange("remarks", e.target.value)}
                  className="mt-1" 
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="font-poppins"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins shadow-md"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save & Sync to Zoho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (Admin Only) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins text-red-600">Delete Client Requirement</AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              Are you sure you want to delete the requirement for <strong>{deletingRequirement?.sampleNo}</strong>?
              <br /><br />
              This will permanently remove the record from Zoho Creator. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 font-poppins"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* R&D Form Dialog */}
      <Dialog open={showRDFormDialog} onOpenChange={setShowRDFormDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl text-slate-800">
              Research and Development Form
            </DialogTitle>
          </DialogHeader>

          {loadingNewStatus ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-slate-600">Loading requirements...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Sample Selection Table */}
              {!selectedRDSample ? (
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Select a Sample (Status: New) - {newStatusRequirements.length} records
                  </Label>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-poppins text-xs">Sample No</TableHead>
                          <TableHead className="font-poppins text-xs">Company</TableHead>
                          <TableHead className="font-poppins text-xs">Product Type</TableHead>
                          <TableHead className="font-poppins text-xs">Category</TableHead>
                          <TableHead className="font-poppins text-xs">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {newStatusRequirements.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                              No requirements with &quot;New&quot; status found
                            </TableCell>
                          </TableRow>
                        ) : (
                          newStatusRequirements.map((req) => (
                            <TableRow 
                              key={req.sampleNo} 
                              className="hover:bg-slate-50 cursor-pointer"
                              onClick={() => handleRDSampleSelect(req)}
                            >
                              <TableCell className="font-medium text-blue-600">{req.sampleNo}</TableCell>
                              <TableCell>{req.companyName || req.clientName}</TableCell>
                              <TableCell>{req.productType || "-"}</TableCell>
                              <TableCell>{req.category || "-"}</TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRDSampleSelect(req);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Selected Sample Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Selected Sample</p>
                        <p className="text-lg font-bold text-slate-800">{selectedRDSample.sampleNo}</p>
                        <p className="text-sm text-slate-600">{selectedRDSample.companyName || selectedRDSample.clientName}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setSelectedRDSample(null);
                          setRDFormData({
                            sampleNo: "",
                            sampleLookupId: "",
                            productName: "",
                            productLookupId: "",
                            testerName: "",
                            testerLookupId: "",
                            testPriority: ""
                          });
                        }}
                      >
                        Change Sample
                      </Button>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Sample No - Disabled */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Sample No</Label>
                      <Input
                        value={rdFormData.sampleNo}
                        disabled
                        className="bg-slate-100 mt-1"
                        data-testid="rd-form-sample-no"
                      />
                    </div>

                    {/* SKU */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">SKU</Label>
                      <Input
                        value={rdFormData.sku}
                        onChange={(e) => setRDFormData(prev => ({ ...prev, sku: e.target.value }))}
                        placeholder="Enter SKU"
                        className="mt-1"
                        data-testid="rd-form-sku"
                      />
                    </div>

                    {/* Product Name - Lookup */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Product Name <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={rdFormData.productName}
                        onValueChange={(value) => {
                          const selected = productList.find(p => p.productName === value);
                          setRDFormData(prev => ({
                            ...prev,
                            productName: value,
                            productLookupId: selected?.id || ""
                          }));
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid="rd-form-product-name">
                          <SelectValue placeholder={loadingProducts ? "Loading..." : "Select Product"} />
                        </SelectTrigger>
                        <SelectContent>
                          {productList.map((product) => (
                            <SelectItem key={product.id} value={product.productName}>
                              {product.productName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tester Name - Lookup */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Tester Name <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={rdFormData.testerName}
                        onValueChange={(value) => {
                          const selected = labEmployees.find(e => e.name === value);
                          setRDFormData(prev => ({
                            ...prev,
                            testerName: value,
                            testerLookupId: selected?.id || ""
                          }));
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid="rd-form-tester-name">
                          <SelectValue placeholder={loadingTesters ? "Loading..." : "Select Tester"} />
                        </SelectTrigger>
                        <SelectContent>
                          {labEmployees.map((employee) => (
                            <SelectItem key={employee.id} value={employee.name}>
                              {employee.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Test Priority */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">
                        Test Priority <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={rdFormData.testPriority}
                        onValueChange={(value) => setRDFormData(prev => ({ ...prev, testPriority: value }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="rd-form-test-priority">
                          <SelectValue placeholder="Select Test Priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stability">Stability</SelectItem>
                          <SelectItem value="Efficacy">Efficacy</SelectItem>
                          <SelectItem value="Safety">Safety</SelectItem>
                          <SelectItem value="Shelf Life">Shelf Life</SelectItem>
                          <SelectItem value="Performance">Performance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* File Upload */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700">File Upload</Label>
                      <Input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          setRDFormData(prev => ({ ...prev, file: file || null }));
                        }}
                        className="mt-1"
                        data-testid="rd-form-file-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                      />
                      {rdFormData.file && (
                        <p className="text-xs text-slate-500 mt-1">
                          Selected: {rdFormData.file.name}
                        </p>
                      )}
                    </div>

                    {/* Remarks */}
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium text-slate-700">Remarks</Label>
                      <Textarea
                        value={rdFormData.remarks}
                        onChange={(e) => setRDFormData(prev => ({ ...prev, remarks: e.target.value }))}
                        placeholder="Enter any additional remarks..."
                        className="mt-1 min-h-[80px]"
                        data-testid="rd-form-remarks"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRDFormDialog(false);
                setSelectedRDSample(null);
                setRDFormData({
                  sampleNo: "",
                  sampleLookupId: "",
                  productName: "",
                  productLookupId: "",
                  testerName: "",
                  testerLookupId: "",
                  testPriority: "",
                  sku: "",
                  remarks: "",
                  file: null
                });
              }}
              className="font-poppins"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            {selectedRDSample && (
              <Button
                onClick={handleSubmitRDForm}
                disabled={submittingRDForm || !rdFormData.productName || !rdFormData.testerName || !rdFormData.testPriority}
                className="bg-blue-600 hover:bg-blue-700 text-white font-poppins"
                data-testid="rd-form-submit-btn"
              >
                {submittingRDForm ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Submit R&D Form
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
